import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LoggerService, type LogLevel } from '../../../core/services/logger.service';
import { CheckboxComponent } from '../../../shared/components/form-controls/checkbox/checkbox.component';
import { InputComponent } from '../../../shared/components/form-controls/input/input.component';
import { SelectComponent } from '../../../shared/components/form-controls/select/select.component';

/**
 * UI component for managing logging levels across different namespaces
 * Allows developers to control log verbosity at runtime without window.console filtering
 */
@Component({
  selector: 'app-log-controls',
  templateUrl: './log-controls.component.html',
  styleUrl: './log-controls.component.css',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, CheckboxComponent, InputComponent, SelectComponent],
})
export class LogControlsComponent {
  loggerService = inject(LoggerService);
  private cdr = inject(ChangeDetectorRef);

  customNamespace = signal('');
  showTabIdEnabled = signal(this.loggerService.isShowTabIdEnabled());

  // Search functionality
  searchTerm = signal('');
  searchTermValue = ''; // For ngModel binding
  private searchTermSubject = new Subject<string>();

  // Common namespaces in the application
  readonly commonNamespaces = [
    'app',
    'auth',
    'auth-guard',
    'permission-guard',
    'permissions',
    'eruda',
    'custom-sync',
    'database',
    'network',
    'address',
    'toast',
    'settings',
    'translation',
  ];

  readonly levels: LogLevel[] = ['trace', 'debug', 'info', 'warn', 'error', 'silent'];

  /**
   * Get all namespaces - merges configured, registered, and common namespaces
   */
  allNamespaces = computed(() => {
    // Get all namespaces saved in localStorage
    const configured = this.loggerService.getConfiguredNamespaces();

    // Get all registered namespaces (from active loggers)
    const registered = this.loggerService.getAllNamespaces();

    // Merge configured, registered, and common namespaces
    const combined = new Set([...configured, ...registered, ...this.commonNamespaces]);

    // Remove 'default' which is the default level setting, not a namespace
    combined.delete('default');

    return Array.from(combined).sort();
  });

  /**
   * Filtered namespaces based on search term
   */
  filteredNamespaces = computed(() => {
    const namespaces = this.allNamespaces();
    const term = this.searchTerm();

    if (!term || term.length < 2) {
      return namespaces;
    }

    const lowerTerm = term.toLowerCase();
    return namespaces.filter((ns) => ns.toLowerCase().includes(lowerTerm));
  });

  constructor() {
    // Set up debounced search
    this.searchTermSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => this.searchTerm.set(term));
  }

  /**
   * Get current log level for a namespace
   */
  getLevel(namespace: string): LogLevel {
    return this.loggerService.getLevel(namespace);
  }

  /**
   * Set log level for a namespace
   */
  setLevel(namespace: string, level: LogLevel): void {
    this.loggerService.setLevel(namespace, level);
  }

  /**
   * Set all namespaces to the same log level
   */
  setAllLevels(level: LogLevel): void {
    this.loggerService.setAllLevels(level, this.allNamespaces());
    // Trigger change detection to update UI with new values
    this.cdr.detectChanges();
  }

  /**
   * Add a custom namespace to the logging controls
   */
  addCustomNamespace(namespaceName: string): void {
    const trimmed = namespaceName.trim();
    if (!trimmed) return;

    // Set the namespace to trace level by default when adding
    this.loggerService.setLevel(trimmed, 'trace');
    this.customNamespace.set('');
    this.cdr.detectChanges();
  }

  /**
   * Handle search input changes (debounced)
   */
  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTermSubject.next(value);
  }

  /**
   * Clear search term
   */
  clearSearch(): void {
    this.searchTermValue = '';
    this.searchTerm.set('');
  }

  toggleShowTabId(enabled: boolean): void {
    this.loggerService.setShowTabIdEnabled(enabled);
    this.showTabIdEnabled.set(this.loggerService.isShowTabIdEnabled());
  }
}
